"use client";

import { useCallback, useEffect, type RefObject } from "react";

/**
 * THE PROFILE CARD'S KEYBOARD CONTRACT — Tab in, Shift+Tab back, Escape out
 *
 * `ProfileHoverCardPanel` announces `role="dialog"`, and a dialog promises a
 * keyboard user a way in and a way out. THERE ARE TWO TRIGGERS that open that
 * panel — `ProfileHoverCard` (wrapping a React child) and `PostHtml` (off a
 * raw `.mention` anchor inside cooked HTML) — and until OSO-512 only the first
 * one kept the promise. The role was set on the shared panel, so the second
 * instance announced itself as a dialog that could be neither entered nor
 * dismissed from the keyboard.
 *
 * So the contract lives here, once, and both triggers install it. A promise
 * made by a shared component cannot be kept by one of its callers.
 *
 * WHY A `onKeyDown` PROP AND NOT A DOCUMENT LISTENER
 * REACT'S SYNTHETIC EVENTS BUBBLE THROUGH THE REACT TREE, NOT THE DOM TREE.
 * The card is `createPortal`ed to `document.body`, so in the DOM it is nowhere
 * near its trigger — but it is rendered as a child of the trigger's component,
 * so a keydown inside the card still reaches a handler above it in the React
 * tree. That is what makes a hand-off possible without a focus trap, a second
 * listener or a `document`-level hook.
 *
 * It is also the one thing a caller has to get right: the returned `onKeyDown`
 * must sit on an element that is an ancestor OF BOTH the trigger and the
 * portal in the REACT tree. Putting it on the element that merely contains the
 * trigger catches Tab going in and misses Shift+Tab coming back out.
 *
 * `restoreFocus` IS A CALLBACK, NOT A REF
 * The obvious shape is a ref holding the element focus came from, and it suits
 * only one of the two callers. `ProfileHoverCard` wraps a React child and can
 * simply keep the node. `PostHtml` does not own its triggers at all: they are
 * `.mention` anchors inside `dangerouslySetInnerHTML`, so it identifies the
 * one to return to by `data-user-id` and re-queries it.
 *
 * (It re-queries rather than holding the node for a reason that no longer
 * applies but is worth knowing: React 19 rebuilt that subtree on every state
 * change until the `dangerouslySetInnerHTML` prop object was memoised — see
 * the note on `cookedHtml` in PostHtml.tsx. Looking the anchor up when it is
 * needed costs one `querySelector` and cannot be undone by a re-render.)
 *
 * ESCAPE IS DELIBERATELY OUTSIDE THE ESCAPE REGISTRY
 * This reads neither `useEscapeLayer`'s registry nor `e.defaultPrevented`, so
 * an Escape aimed at something else — a docked chat window, a reply being
 * cancelled, the lightbox this card was hovered inside — also closes the card.
 * That is the existing, deliberate behaviour and the cost is written out in
 * full in `lib/hooks/useEscapeLayer.ts`. Read that note before making this a
 * layer: registering the card is what six OSO-434 rounds kept reverting.
 */
export function useHoverCardKeyboard({
  open,
  cardRef,
  restoreFocus,
  close,
  isTrigger,
}: {
  /** Whether the card is currently rendered. Nothing is installed while closed. */
  open: boolean;
  /** The panel element, via `panelRef` on `ProfileHoverCardPanel`. */
  cardRef: RefObject<HTMLElement | null>;
  /** Put focus back where it was when the card opened. See the note above on
   *  why this is a callback and not the element itself. */
  restoreFocus: () => void;
  close: () => void;
  /**
   * Whether a keydown OUTSIDE the card came from the card's trigger. Only
   * then does Tab hand focus in.
   *
   * `ProfileHoverCard` wraps exactly its trigger, so everything outside the
   * card is the trigger and it leaves this unset. `PostHtml` installs the
   * handler on a wrapper around the WHOLE cooked body, where an ordinary link
   * is as much "outside the card" as the mention is — without this, a Tab
   * from any link in the post was swallowed and sent into the card while it
   * was open (OSO-516).
   */
  isTrigger?: (target: Element) => boolean;
}): { onKeyDown: (e: React.KeyboardEvent) => void } {
  /**
   * THE FIRST focusable thing, and there may be more than one: "Send besked"
   * when `can.message`, and the affiliation chip's link when the profile has
   * an affiliation. Shift+Tab from anywhere inside hands back to the trigger,
   * so a second control is reachable going forwards and skipped going back —
   * which is the same hand-off, not a trap.
   */
  const cardTarget = useCallback(
    () =>
      cardRef.current?.querySelector<HTMLElement>(
        "button:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])",
      ) ?? null,
    [cardRef],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== "Tab" || !open) return;
      const card = cardRef.current;
      const target = cardTarget();
      if (!card || !target) return;
      const inCard = card.contains(e.target as Node);
      if (!e.shiftKey && !inCard) {
        if (isTrigger && !isTrigger(e.target as Element)) return;
        e.preventDefault();
        target.focus();
      } else if (e.shiftKey && inCard) {
        e.preventDefault();
        restoreFocus();
      }
    },
    [open, cardRef, cardTarget, restoreFocus, isTrigger],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Escape while focus is INSIDE the card would otherwise unmount the
      // focused element and drop focus to <body> — the keyboard user loses
      // their place in the page. Put it back on the trigger.
      if (cardRef.current?.contains(document.activeElement)) {
        restoreFocus();
      }
      close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, cardRef, restoreFocus, close]);

  return { onKeyDown };
}
